import type { SearchOption } from '@/types/search';
import type { WrappedRowColumn } from '../common/handsontable.config';

// Column indices constants
export const STT_COLUMN_INDEX = 0;
export const PLANT_COLUMN_INDEX = 2; // ブランド (Plant name)
export const WITNESS_COMPANY_COLUMN_INDEX = 8; // 立会会社 (line2 physical column)
export const PITCH_HOPE_COLUMN_INDEX = 16;
export const PITCH_POSSIBLE_COLUMN_INDEX = 17;
export const WITNESS_COMPANY_ID_COLUMN_INDEX = 19;
// 1-line indices: remarks (社内) = 21, remarksForClient (ゼネコン) = 22
export const REMARKS_COLUMN_INDEX = 21;
export const REMARKS_FOR_CLIENT_COLUMN_INDEX = 22;
export const EVENT_CLICK_MOUSE = 2; //Right-click event button code

// Columns that require string conversion
export const NAME_COLUMNS = [PLANT_COLUMN_INDEX, WITNESS_COMPANY_COLUMN_INDEX];

/**
 * Calculate wrapped row index from absolute row number
 * @param row - Absolute row index
 * @param wrappedRowColumnTableLength - Length of wrapped row column table
 * @returns Wrapped row index (-1 for line 1, 0+ for wrapped rows)
 */
export const wrappedRowIndex = (row: number, wrappedRowColumnTableLength: number): number => {
  return (row % (wrappedRowColumnTableLength + 1)) - 1;
};

/**
 * Map 1-line validation index (rowsWithErrors.field.index) to 3-line table cell coordinates.
 * This mirrors the inverse mapping used when converting 3-line edits back to 1-line indices.
 */
export const mapOneLineValidationColumnTo3LineCell = (
  oneLineColumnIndex: number,
  line1RowIndex: number,
  rowsPerPlan: number
): { row: number; col: number } | null => {
  if (rowsPerPlan < 2) return null;

  // line 1 (3-line row offset 0): one-line 0..11 => col 1..12
  if (oneLineColumnIndex >= 0 && oneLineColumnIndex <= 11) {
    return { row: line1RowIndex, col: oneLineColumnIndex + 1 };
  }

  // line 2 (3-line row offset +1) with placeholders at col 5, 9, 11.
  const line2PhysicalCol = new Map<number, number>([
    [12, 1], // 車両
    [13, 2], // 先行材 種類
    [14, 3], // 先行材 数量
    [15, 4], // ピッチ希望
    [16, 6], // ピッチ可能
    [17, 7], // 打設方法
    [18, 8], // 立会会社
    [19, 10], // 氏名
    [20, 12], // 連絡先
  ]);

  const mappedLine2Col = line2PhysicalCol.get(oneLineColumnIndex);
  if (mappedLine2Col !== undefined) {
    return { row: line1RowIndex + 1, col: mappedLine2Col };
  }

  // line 3 uses merged blocks.
  const line3RowIndex = line1RowIndex + (rowsPerPlan - 1);
  if (oneLineColumnIndex === 21) return { row: line3RowIndex, col: 1 }; // 備考 社内
  if (oneLineColumnIndex === 22) return { row: line3RowIndex, col: 7 }; // 備考 ゼネコン

  return null;
};

/**
 * Get company name by ID from options
 * Note: SearchOption format is { key: id, value: name, label: name }
 */
export const getCompanyNameById = (
  id: string | null | undefined,
  options: SearchOption[]
): string => {
  if (!id) return '';
  // Use opt.key to match ID (not opt.value which is the name)
  const option = options.find((opt) => opt.key === String(id));
  return option?.label ?? '';
};

/**
 * Get company ID by name from options
 * Note: SearchOption format is { key: id, value: name, label: name }
 */
export const getCompanyIdByName = (
  name: string | null | undefined,
  options: SearchOption[]
): string | null => {
  if (!name) return null;
  // Match by label (display name)
  const option = options.find((opt) => opt.label === name);
  if (!option?.key) return null;
  // Return the ID (key), not value
  return String(option.key);
};

/**
 * Ensure wrapped row column table has correct length with undefined placeholders
 */
export const ensureWrappedRowColumnTableLength = (
  wrappedRowColumnTable: WrappedRowColumn[][]
): WrappedRowColumn[][] => {
  return wrappedRowColumnTable.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return [
        row[0],
        row[1],
        row[2],
        row[3],
        undefined,
        row[4],
        row[5],
        row[6],
        undefined,
        row[7],
        undefined,
        row[8],
      ];
    }
    if (rowIndex === 1) {
      return [
        row[0],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        row[1],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
      ];
    }
    return row;
  });
};

/**
 * Check if a merge configuration is valid
 */
export const isValidMerge = (merge: { col: number; colspan: number }): boolean => {
  const endCol = merge.col + merge.colspan - 1;
  return merge.col >= 1 && merge.col <= 12 && endCol >= merge.col && endCol <= 12;
};

/**
 * Create text span element with word-wrap styles for line 2 cells
 */
export const createTextSpanWithStyles = (content: string): HTMLSpanElement => {
  const textSpan = document.createElement('span');
  textSpan.style.flex = '1';
  textSpan.style.wordWrap = 'break-word';
  textSpan.style.whiteSpace = 'normal';
  textSpan.style.overflowWrap = 'break-word';
  textSpan.textContent = content;
  return textSpan;
};

/**
 * Create text span element with word-wrap styles and HTML content for line 2 cells
 */
export const createTextSpanWithHTML = (htmlContent: string): HTMLSpanElement => {
  const textSpan = document.createElement('span');
  textSpan.style.flex = '1';
  textSpan.style.wordWrap = 'break-word';
  textSpan.style.whiteSpace = 'normal';
  textSpan.style.overflowWrap = 'break-word';
  textSpan.innerHTML = htmlContent;
  return textSpan;
};
